import { parseColor } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';

import Button from 'ui/Button';
import ColorPicker from 'ui/ColorPicker';
import Field from 'ui/Field';
import Icon from 'ui/Icon';
import Input from 'ui/Input';
import { HStack, VStack } from 'ui/Layout';
import Tooltip from 'ui/Tooltip';
import { Text } from 'ui/Typography';

import { TeamSettings } from '../../utils/team';
import ColorKeywordsEditor from './ColorKeywordsEditor';

const MIN_BRAND_COLORS = 3;
const MAX_BRAND_COLORS = 15;

type Props = {
  form: ReturnType<typeof useForm<TeamSettings>>;
};

const CorporateBrandingSettingsContent: React.FC<Props> = ({ form }) => {
  const formBrandColors = form.watch('brand.colors') ?? [];

  const belowMaximumColors = formBrandColors.length < MAX_BRAND_COLORS;
  const aboveMinimumColors = formBrandColors.length > MIN_BRAND_COLORS;

  return (
    <>
      <Text color="gray.700" fontSize="md" fontWeight="medium" mb={4}>
        Colors
      </Text>
      <VStack alignItems="stretch" gap={4}>
        {formBrandColors.map((color, index) => (
          <HStack alignItems="flex-start" key={index}>
            <Controller
              name={`brand.colors.${index}.value`}
              control={form.control}
              render={({ field }) => (
                <ColorPicker
                  name={field.name}
                  value={parseColor(field.value)}
                  onValueChange={({ value }) => {
                    field.onChange(value.toString('hex'));
                  }}
                />
              )}
            />
            <Field
              helperText={
                color.isDefault ? (
                  <Tooltip content="This color will be used as the default color for your diagrams">
                    <Text color="gray.800" cursor="help" fontWeight="medium">
                      Default color
                    </Text>
                  </Tooltip>
                ) : (
                  <ColorKeywordsEditor form={form} colorIndex={index} />
                )
              }
            >
              <Input
                {...form.register(`brand.colors.${index}.label`)}
                placeholder="Color name"
                size="sm"
              />
            </Field>
            <Tooltip
              content={
                color.isDefault
                  ? "Default color can't be removed"
                  : !aboveMinimumColors
                    ? `Your color branding must include at least ${MIN_BRAND_COLORS} colors`
                    : 'Remove color'
              }
            >
              <Button
                variant="danger-ghost"
                onClick={() => {
                  form.setValue(
                    'brand.colors',
                    formBrandColors.filter((_, i) => i !== index),
                    { shouldDirty: true }
                  );
                }}
                disabled={color.isDefault || !aboveMinimumColors}
                size="xs"
              >
                <Icon icon="trash-can-regular" />
              </Button>
            </Tooltip>
          </HStack>
        ))}
        <Tooltip
          content={`Your color branding can't include more than ${MAX_BRAND_COLORS} colors`}
          disabled={belowMaximumColors}
        >
          <Button
            variant="primary-ghost"
            onClick={() => {
              form.setValue(
                'brand.colors',
                [
                  ...formBrandColors,
                  { value: '#000000', label: '', keywords: [] },
                ],
                { shouldDirty: true }
              );
            }}
            disabled={!belowMaximumColors}
            size="sm"
            mt={2}
          >
            <Icon icon="plus" /> Add color
          </Button>
        </Tooltip>
      </VStack>
    </>
  );
};

export default CorporateBrandingSettingsContent;
